import { AssetType } from "lib/types/assets/AssetType";
import { SearchToolSnapshot } from "components/elements/CesiumViewer/core/wrapper/AssetsPropertiesSearchToolPlugin";

export type ProjectLayerVersionPolicy = "Unknown" | "Latest" | "Pinned";

/**
 * The two policies the UI can actually act on. `Unknown` is the API's
 * uninitialised value and is never a valid selection.
 */
export type SettableVersionPolicy = Exclude<
  ProjectLayerVersionPolicy,
  "Unknown"
>;

/**
 * Narrows a policy from the API to one the UI can render, treating anything
 * that is not an explicit `Pinned` as `Latest` — matching the server-side
 * default for layers created before the policy existed.
 */
export function normaliseVersionPolicy(
  value: ProjectLayerVersionPolicy | undefined,
): SettableVersionPolicy {
  return value === "Pinned" ? "Pinned" : "Latest";
}

export type ProjectLayerConstituentAssetModel = {
  assetId: number;
  assetVersionId: number;
};

export type ProjectLayerRenderAssetModel = {
  assetVersionId: number;
  contentUri: string | null;
  vectorContentUri: string | null;
  assetType: AssetType;
};

export type ProjectLayerModel = {
  id: number;
  assetId: number;
  projectId: number;
  name: string;
  versionPolicy: ProjectLayerVersionPolicy;
  searchesStateJson?: SearchToolSnapshot | null;
  createdOn: Date;
  createdBy: string;
  canManage: boolean;

  /**
   * The layer's constituent asset references, the raw edit-time list of which assets
   * are in this layer and which version of each. Describes the layer's composition.
   */
  constituentAssets: ProjectLayerConstituentAssetModel[];

  /**
   * The unified asset list for this layer, used for both rendering and view capture.
   * Contains the baked tileset entry when baked, or all constituent entries when
   * unbaked. Assets that are not included in baking are always included individually.
   */
  renderAssets: ProjectLayerRenderAssetModel[];
};
